/**
 * Equipment Tietopohja detail shell for `/admin/knowledge-base`.
 * Wires a type-specific live adapter into the shared `KbEquipmentIaBody`.
 */
import type { ReactNode } from "react";
import type { KBEquipmentType } from "@/lib/api/agent";
import {
  AIR_HANDLING_UNIT_TYPE,
  behaviorIdsUnderComponentFromKb as ahuBehaviorIdsUnderComponentFromKb,
  buildDependencyGroupsFromKb as buildAhuDependencyGroupsFromKb,
  buildPathChainsFromKb as buildAhuPathChainsFromKb,
  buildUnitLevelSignalsFromKb as buildAhuUnitLevelSignalsFromKb,
  componentAirPathsFromKb as ahuComponentAirPathsFromKb,
  dossierSignalsForAhuComponent,
} from "@/lib/equipment-kb/air-handling-unit";
import { VARIABLE_AIR_VOLUME_BOX_TYPE } from "@/lib/equipment-kb/variable-air-volume-box";
import {
  behaviorIdsUnderComponentFromKb,
  buildDependencyGroupsFromKb,
  buildPathChainsFromKb,
  buildUnitLevelSignalsFromKb,
  componentAirPathsFromKb,
  dossierSignalsForOwnedComponent,
} from "@/lib/equipment-kb/generic";
import {
  KbEquipmentIaBody,
  type EquipmentKbIaAdapter,
} from "@/components/admin/KbEquipmentIaBody";
import { cn } from "@/lib/utils";
import { cardBase } from "@/lib/card-styles";

export { AIR_HANDLING_UNIT_TYPE, VARIABLE_AIR_VOLUME_BOX_TYPE };

const airHandlingUnitAdapter: EquipmentKbIaAdapter = {
  buildPathChains: buildAhuPathChainsFromKb,
  buildDependencyGroups: buildAhuDependencyGroupsFromKb,
  buildUnitSignals: buildAhuUnitLevelSignalsFromKb,
  componentAirPaths: ahuComponentAirPathsFromKb,
  behaviorIdsUnderComponent: ahuBehaviorIdsUnderComponentFromKb,
  dossierSignalsForComponent: dossierSignalsForAhuComponent,
};

// eslint-disable-next-line react-refresh/only-export-components
export const genericEquipmentKbAdapter: EquipmentKbIaAdapter = {
  buildPathChains: buildPathChainsFromKb,
  buildDependencyGroups: buildDependencyGroupsFromKb,
  buildUnitSignals: buildUnitLevelSignalsFromKb,
  componentAirPaths: componentAirPathsFromKb,
  behaviorIdsUnderComponent: behaviorIdsUnderComponentFromKb,
  dossierSignalsForComponent: dossierSignalsForOwnedComponent,
};

// AHU is the only equipment type with its own topology adapter (air-path
// interface ports, power-facet rules). Every other type — including
// Lämmityskattila, Lämpöpumppu, Poistoilmapuhallin, and any future type —
// reads the live `GET /kb/equipment/{type}` payload through the generic
// adapter; there is no other fallback (the old flat Tietopohja body is
// retired).
const ADAPTERS_BY_TYPE: Record<string, EquipmentKbIaAdapter> = {
  [AIR_HANDLING_UNIT_TYPE]: airHandlingUnitAdapter,
};

// eslint-disable-next-line react-refresh/only-export-components
export function equipmentKbIaAdapterFor(
  equipmentType: string,
): EquipmentKbIaAdapter {
  return ADAPTERS_BY_TYPE[equipmentType] ?? genericEquipmentKbAdapter;
}

export function EquipmentKbDetail({
  header,
  kb,
  adapter,
}: {
  header: ReactNode;
  kb: KBEquipmentType;
  adapter: EquipmentKbIaAdapter;
}) {
  return (
    <div className={cn(cardBase, "overflow-hidden h-full flex flex-col")}>
      {header}
      <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">
        <KbEquipmentIaBody kb={kb} adapter={adapter} />
      </div>
    </div>
  );
}
